import CustomDatePicker from '@/components/shared/CustomDatePicker';
import React, { useState, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { base44 } from '@/api/base44Client';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { Card, CardContent } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useBatchSelection } from '@/hooks/useBatchSelection';
import BatchActionBar from '@/components/ui/BatchActionBar';
import { Plus, Truck, ChevronDown, ChevronUp, Pencil, Trash2, FileDown, Package, UserCircle2, Navigation, Banknote, StickyNote, Printer } from 'lucide-react';
import BarangMasukForm from '@/components/barangmasuk/BarangMasukForm';
import BarangMasukEditForm from '@/components/delivery/BarangMasukEditForm';
import CetakSampleStokModal from '@/components/stok/CetakSampleStokModal';
import { format } from 'date-fns';
import { id } from 'date-fns/locale';
import LastUpdated from '@/components/ui/LastUpdated';
import { exportToPDF } from '@/utils/exportPDF';
import { useSession } from '@/lib/SessionContext';
import { canManageDelivery } from '@/lib/AuthSession';

export default function BarangMasukTab() {
  const queryClient = useQueryClient();
  const [showForm, setShowForm] = useState(false);
  const [showCetakSampleModal, setShowCetakSampleModal] = useState(false);
  const [editRecord, setEditRecord] = useState(null);
  const [expanded, setExpanded] = useState(null);
  const [search, setSearch] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');

  // Pagination State
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const scrollToTop = () => window.scrollTo({ top: 0, behavior: 'smooth' });

  const { member } = useSession();
  const canEdit = canManageDelivery(member);

  const { data: records = [], isLoading } = useQuery({
    queryKey: ['barang-masuk'],
    queryFn: () => base44.entities.BarangMasuk.list('-created_date'),
  });

  const deleteMutation = useMutation({
    mutationFn: (id) => base44.entities.BarangMasuk.delete(id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['barang-masuk'] }),
  });

  const filtered = records.filter(r => {
    const q = search.toLowerCase();
    const matchSearch = !search ||
      r.nomor_referensi?.toLowerCase().includes(q) ||
      r.supplier?.toLowerCase().includes(q) ||
      r.pic?.toLowerCase().includes(q) ||
      r.catatan?.toLowerCase().includes(q) ||
      r.ekspedisi?.toLowerCase().includes(q) ||
      (r.items || []).some(it => 
        it.nama_barang?.toLowerCase().includes(q) ||
        it.nama?.toLowerCase().includes(q) ||
        it.kategori?.toLowerCase().includes(q) ||
        it.vendor?.toLowerCase().includes(q)
      );
    const matchFrom = !dateFrom || (r.tanggal && r.tanggal >= dateFrom);
    const matchTo = !dateTo || (r.tanggal && r.tanggal <= dateTo);
    return matchSearch && matchFrom && matchTo;
  });

  const handleExport = () => {
    const subtitle = (dateFrom || dateTo)
      ? `Periode: ${dateFrom || '...'} s/d ${dateTo || '...'} · ${filtered.length} data`
      : `Total ${filtered.length} catatan penerimaan`;
    exportToPDF({
      title: 'Laporan Barang Masuk',
      subtitle,
      filename: 'Barang-Masuk',
      columns: [
        { key: 'nomor_referensi', label: 'No. Referensi' },
        { key: 'tanggal', label: 'Tanggal', render: r => r.tanggal ? format(new Date(r.tanggal + 'T00:00:00'), 'dd/MM/yyyy') : '-' },
        { key: 'supplier', label: 'Supplier', render: r => r.supplier || '-' },
        { key: 'ekspedisi', label: 'Ekspedisi', render: r => r.ekspedisi || '-' },
        { key: 'ongkir', label: 'Ongkir', render: r => {
          if (!r.ongkir || r.ongkir <= 0) return '-';
          const dibayar = r.ongkir_dibayar_oleh === 'Perusahaan' ? 'dari Perusahaan' : r.ongkir_dibayar_oleh === 'Supplier' ? 'dari Supplier' : r.ongkir_dibayar_oleh === 'Split' ? '(Split)' : '';
          return `Rp ${r.ongkir.toLocaleString('id-ID', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ${dibayar}`;
        }},
        { key: 'items', label: 'Jml Item', render: r => `${r.items?.length || 0} item` },
        { key: 'items_detail', label: 'Detail Barang', render: r => (r.items || []).map(i => `${i.nama_barang} (+${i.jumlah} ${i.satuan})`).join(', ') || '-' },
        { key: 'pic', label: 'PIC', render: r => r.pic || '-' },
        { key: 'catatan', label: 'Catatan', render: r => r.catatan || '-' },
      ],
      rows: filtered,
    });
  };

  const handleDelete = (record) => {
    if (!confirm(`Hapus data barang masuk "${record.nomor_referensi || 'ini'}"? Perubahan stok tidak akan dikembalikan.`)) return;
    deleteMutation.mutate(record.id);
  };

  const {
    selectedIds,
    isSelectionMode,
    toggleSelection,
    selectAll,
    clearSelection,
    startSelection,
    selectedCount
  } = useBatchSelection(filtered, o => o.id);

  const handleBulkDelete = async () => {
    if (!confirm(`Hapus ${selectedCount} data barang masuk terpilih? Perubahan stok tidak akan dikembalikan.`)) return;
    await Promise.all(Array.from(selectedIds).map(id => deleteMutation.mutateAsync(id)));
    clearSelection();
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-col md:flex-row gap-3 md:gap-2 items-stretch md:items-center">
        <Input
          placeholder="Cari nama item, supplier, no referensi..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="w-full md:w-64 h-9 md:h-10 text-xs md:text-sm"
        />
        <div className="flex flex-row items-center gap-2">
          <div className="flex items-center gap-1.5 flex-1 md:flex-none">
            <span className="text-[11px] md:text-xs text-muted-foreground whitespace-nowrap hidden sm:inline">Dari:</span>
            <CustomDatePicker value={dateFrom} onChange={setDateFrom} className="w-full md:w-36 h-9 text-xs" />
          </div>
          <div className="flex items-center gap-1.5 flex-1 md:flex-none">
            <span className="text-[11px] md:text-xs text-muted-foreground whitespace-nowrap hidden sm:inline">S/d:</span>
            <CustomDatePicker value={dateTo} onChange={setDateTo} className="w-full md:w-36 h-9 text-xs" />
          </div>
          {(dateFrom || dateTo) && (
            <Button variant="ghost" size="sm" className="text-xs h-9 px-2 shrink-0" onClick={() => { setDateFrom(''); setDateTo(''); }}>Reset</Button>
          )}
        </div>
        <div className="flex gap-2 shrink-0 w-full md:w-auto md:ml-auto">
          <Button
            type="button"
            variant="outline"
            onClick={() => setShowCetakSampleModal(true)}
            className="gap-1.5 flex-1 md:flex-none h-9 text-xs md:text-sm border-[#8C6208]/40 bg-[#FAF7F2] hover:bg-[#F3ECE1] text-[#8C6208] font-bold"
            title="Cetak Stiker Sample (40×20mm) untuk kedatangan barang & bibit"
          >
            <Printer className="w-3.5 h-3.5 md:w-4 md:h-4 text-[#8C6208]" />
            <span>Cetak Label Sample</span>
          </Button>
          <Button onClick={handleExport} variant="outline" className="gap-1.5 flex-1 md:flex-none h-9 text-xs md:text-sm">
            <FileDown className="w-3.5 h-3.5 md:w-4 md:h-4" /> <span className="hidden sm:inline">Export PDF</span><span className="sm:hidden">PDF</span>
          </Button>
          {canEdit && (
            <Button onClick={() => setShowForm(true)} className="gap-1.5 flex-1 md:flex-none h-9 text-xs md:text-sm">
              <Plus className="w-3.5 h-3.5 md:w-4 md:h-4" /> <span className="hidden sm:inline">Input Barang Masuk</span><span className="sm:hidden">Input Masuk</span>
            </Button>
          )}
        </div>
      </div>

      <BatchActionBar
        isSelectionMode={isSelectionMode}
        selectedCount={selectedCount}
        totalCount={filtered.length}
        onSelectAll={selectAll}
        onClearSelection={clearSelection}
        onBatchDelete={handleBulkDelete}
      />

      {isLoading ? (
        <div className="text-center py-8 md:py-12 text-sm text-muted-foreground">Memuat data...</div>
      ) : (
        <div className="grid gap-3">
          {filtered.length === 0 ? (
            <div className="text-center py-8 md:py-12 text-sm text-muted-foreground">Belum ada data barang masuk.</div>
          ) : (
            filtered.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage).map(record => (
              <Card 
                key={record.id}
                className={`transition-all ${
                  isSelectionMode && selectedIds.has(record.id) ? 'ring-2 ring-rose-500 bg-rose-50/20' : ''
                }`}
                onClick={() => isSelectionMode && toggleSelection(record.id)}
              >
                <CardContent className="p-4">
                  <div className="flex items-center justify-between gap-4">
                    <div className="flex items-center gap-3 min-w-0">
                      {isSelectionMode && (
                        <div className="shrink-0 animate-in fade-in slide-in-from-left-2 duration-200" onClick={(e) => e.stopPropagation()}>
                          <Checkbox 
                            checked={selectedIds.has(record.id)} 
                            onCheckedChange={() => toggleSelection(record.id)}
                            className="data-[state=checked]:bg-rose-500 data-[state=checked]:border-rose-500"
                          />
                        </div>
                      )}
                      <div className="w-10 h-10 rounded-lg bg-green-100 text-green-600 flex items-center justify-center shrink-0">
                        <Truck className="w-5 h-5" />
                      </div>
                      <div>
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="font-semibold">{record.nomor_referensi || 'Tanpa Referensi'}</span>
                          <Badge variant="outline" className="text-xs text-green-600 border-green-300">
                            {record.items?.length || 0} item
                          </Badge>
                        </div>
                        <p className="text-sm text-muted-foreground">
                          {record.tanggal ? format(new Date(record.tanggal + 'T00:00:00'), 'dd MMMM yyyy', { locale: id }) : '-'}
                          {record.supplier ? ` • ${record.supplier}` : ''}
                        </p>
                        {record.pic && (
                          <span className="inline-flex items-center gap-1 text-xs bg-primary/10 text-primary px-2 py-0.5 rounded-full font-medium mt-0.5">
                            <UserCircle2 className="w-3 h-3" />{record.pic}
                          </span>
                        )}
                        <LastUpdated date={record.updated_date || record.created_date} className="mt-0.5" />
                      </div>
                    </div>
                    <div className="flex items-center gap-1 shrink-0">
                      {canEdit && !isSelectionMode && !record.isFromPurchasingRequest && (
                        <>
                          <Button variant="ghost" size="icon" className="w-8 h-8 text-muted-foreground hover:text-foreground"
                            onClick={(e) => { e.stopPropagation(); setEditRecord(record); }}>
                            <Pencil className="w-4 h-4" />
                          </Button>
                          <Button variant="ghost" size="icon" className="w-8 h-8 text-muted-foreground hover:text-destructive"
                            onClick={(e) => { e.stopPropagation(); startSelection(record.id); }}>
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </>
                      )}
                      <Button variant="ghost" size="icon" className="w-8 h-8"
                        onClick={() => setExpanded(expanded === record.id ? null : record.id)}>
                        {expanded === record.id ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                      </Button>
                    </div>
                  </div>

                  {expanded === record.id && (
                    <div className="mt-4 border-t pt-4 space-y-3">
                      {/* Info ekspedisi, ongkir & PIC */}
                      {(record.ekspedisi || record.ongkir > 0 || record.pic) && (
                        <div className="flex flex-wrap gap-2 text-xs">
                          {record.ekspedisi && (
                            <span className="inline-flex items-center gap-1.5 bg-slate-100 text-slate-700 px-2.5 py-1 rounded-full font-medium">
                              <Navigation className="w-3 h-3" />{record.ekspedisi}
                            </span>
                          )}
                          {record.ongkir > 0 && (
                            <span className="inline-flex items-center gap-1.5 bg-emerald-50 text-emerald-700 px-2.5 py-1 rounded-full font-medium">
                              <Banknote className="w-3 h-3" />Rp {record.ongkir.toLocaleString('id-ID', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                              {record.ongkir_dibayar_oleh && <span className="text-emerald-500">· {record.ongkir_dibayar_oleh}</span>}
                            </span>
                          )}
                          {record.pic && (
                            <span className="inline-flex items-center gap-1.5 bg-primary/10 text-primary px-2.5 py-1 rounded-full font-medium">
                              <UserCircle2 className="w-3 h-3" />{record.pic}
                            </span>
                          )}
                        </div>
                      )}
                      {record.catatan && <p className="text-sm text-muted-foreground italic">Catatan: {record.catatan}</p>}
                      {record.items?.length > 0 && (
                        <>
                          <div className="grid grid-cols-3 text-xs font-semibold text-muted-foreground pb-1 border-b">
                            <span>Barang</span>
                            <span className="text-center">Kategori</span>
                            <span className="text-right">Jumlah</span>
                          </div>
                          {record.items.map((item, idx) => (
                            <div key={idx} className="grid grid-cols-3 text-sm">
                              <span>{item.nama_barang}</span>
                              <span className="text-center">
                                <Badge variant="outline" className="text-xs">{item.kategori || 'Bahan'}</Badge>
                              </span>
                              <span className="text-right font-medium text-green-600">+{item.jumlah} {item.satuan}</span>
                            </div>
                          ))}
                        </>
                      )}
                    </div>
                  )}
                </CardContent>
              </Card>
            ))
          )}

          {/* ── Pagination Controls ── */}
          {filtered.length > 0 && (
            <div className="px-6 py-4 border border-slate-100 rounded-2xl bg-white flex flex-wrap items-center justify-between gap-4 mt-6">
              <div className="flex items-center gap-3">
                <span className="text-[11px] font-bold text-slate-500">Row per Page</span>
                <div className="w-[80px]">
                  <Select value={String(itemsPerPage)} onValueChange={(val) => { setItemsPerPage(Number(val)); setCurrentPage(1); }}>
                    <SelectTrigger className="h-8 text-[11px] font-bold text-slate-700 bg-slate-50 border-slate-200/80 rounded-xl">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {[5, 10, 25, 50, 100].map(n => (
                        <SelectItem key={n} value={String(n)} className="text-[11px] font-bold">{n}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              
              <div className="flex items-center gap-1.5">
                <button 
                  onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                  disabled={currentPage === 1}
                  className="w-8 h-8 flex items-center justify-center rounded-lg text-slate-400 hover:bg-slate-50 hover:text-[#8C6208] disabled:opacity-30 disabled:hover:bg-transparent disabled:hover:text-slate-400 transition-all font-bold text-sm"
                >
                  &lt;
                </button>
                
                {(() => {
                  const totalPages = Math.ceil(filtered.length / itemsPerPage);
                  const pages = [];
                  for(let i=1; i<=totalPages; i++) {
                    if (i === 1 || i === totalPages || (i >= currentPage - 1 && i <= currentPage + 1)) {
                      pages.push(
                        <button
                          key={i}
                          onClick={() => setCurrentPage(i)}
                          className={`w-8 h-8 flex items-center justify-center rounded-lg text-[11px] font-black transition-all ${
                            currentPage === i 
                              ? 'bg-[#8C6208] text-white shadow-md shadow-[#8C6208]/30' 
                              : 'text-slate-500 hover:bg-[#F9F5EC] hover:text-[#8C6208]'
                          }`}
                        >
                          {i}
                        </button>
                      );
                    } else if (i === currentPage - 2 || i === currentPage + 2) {
                      pages.push(<span key={i} className="text-slate-400 text-[10px] px-1">...</span>);
                    }
                  }
                  return pages;
                })()}
                
                <button 
                  onClick={() => setCurrentPage(p => Math.min(Math.ceil(filtered.length / itemsPerPage), p + 1))}
                  disabled={currentPage >= Math.ceil(filtered.length / itemsPerPage)}
                  className="w-8 h-8 flex items-center justify-center rounded-lg text-slate-400 hover:bg-slate-50 hover:text-[#8C6208] disabled:opacity-30 disabled:hover:bg-transparent disabled:hover:text-slate-400 transition-all font-bold text-sm"
                >
                  &gt;
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Floating Back to Top Button */}
      <button 
        onClick={scrollToTop}
        className="fixed bottom-[80px] md:bottom-8 right-6 md:right-8 z-50 w-12 h-12 bg-[#8C6208] hover:bg-[#6A4A06] text-white rounded-full shadow-xl shadow-[#8C6208]/30 flex items-center justify-center transition-transform hover:-translate-y-1 print:hidden border border-[#D4AF37]/30"
        title="Kembali ke Atas"
      >
        <ChevronUp className="w-6 h-6" />
      </button>

      {showForm && (
        <BarangMasukForm
          onClose={() => setShowForm(false)}
          onSaved={() => {
            setShowForm(false);
            queryClient.invalidateQueries({ queryKey: ['barang-masuk'] });
          }}
        />
      )}

      {editRecord && (
        <BarangMasukEditForm
          record={editRecord}
          onClose={() => setEditRecord(null)}
          onSaved={() => {
            setEditRecord(null);
            queryClient.invalidateQueries({ queryKey: ['barang-masuk'] });
          }}
        />
      )}

      {showCetakSampleModal && (
        <CetakSampleStokModal
          isOpen={showCetakSampleModal}
          onClose={() => setShowCetakSampleModal(false)}
        />
      )}
    </div>
  );
}